import React from 'react';
import { Home, Bath, Square, MapPin, Phone, Mail, Car, Shield, Calendar, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

// Componente para imágenes de propiedades
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
    />
  );

  const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';
  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const BasicHomeFlyer = ({ property }) => {
  // Datos de la propiedad con fallbacks
  const propertyData = property || {
    titulo: 'Hermosas Oficina De Lujo Ideal Para Equipos Grandes',
    tipo: 'alquiler',
    operacion: 'alquiler',
    precio_soles: 45600,
    precio_dolares: 0,
    ubicacion: 'Surquillo, Provincia de Lima, Perú',
    habitaciones: 3,
    banos: 3,
    area_total: 40000,
    telefono: '976115237',
    email: 'guillermoovillosladoo@gmail.com',
    descripcion_corta: 'Alquilo oficinas, en una zona muy bonita de Surquillo con una vista panorámica a la ciudad. Ideal para entornos de equipos grandes. Aprovecha esta gran oportunidad.',
    imagenes: [],
    vendedor: {
      nombre: 'InmobiliARIA',
      foto_perfil: null,
      telefono: '976115237',
      email: 'guillermoovillosladoo@gmail.com'
    }
  };

  // Formato de precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Generate property URL
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property?.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property?.id || 'casa-123'}`;
  };

  // Características principales
  const mainFeatures = [
    { 
      icon: Home,
      label: 'Habitaciones',
      value: propertyData.habitaciones
    },
    { 
      icon: Bath,
      label: 'Baños',
      value: propertyData.banos
    },
    { 
      icon: Square,
      label: 'Área Total',
      value: `${propertyData.area_total}m²`
    },
    { 
      icon: Shield,
      label: 'Seguridad',
      value: '24/7'
    }
  ];

  // Características adicionales
  const additionalFeatures = [
    'Estacionamiento incluido',
    'Área de recepción',
    'Sala de reuniones',
    'Conexión a internet',
    'Servicios básicos incluidos',
    'Seguridad 24 horas'
  ];

  return (
    <div className="max-w-4xl mx-auto overflow-hidden rounded-xl shadow-xl">
      {/* Encabezado con título y tipo de operación */}
      <div className="bg-orange-500 p-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">{propertyData.titulo}</h1>
        <div className="bg-white text-orange-500 px-4 py-2 rounded-full font-bold uppercase text-sm">
          {propertyData.operacion === 'alquiler' ? 'EN ALQUILER' : 'EN VENTA'}
        </div>
      </div>
      
      {/* Imagen principal con precios - Altura estandarizada */}
      <div className="relative h-72">
        <PropertyImage 
          url={propertyData.imagenes[0]}
          alt="Vista principal de la propiedad" 
          className="w-full h-full object-cover"
        />
        
        {/* Etiqueta de precio */}
        <div className="absolute top-6 right-0">
          <div className="bg-orange-500 text-white py-3 px-6">
            <p className="text-sm font-medium uppercase">PRECIO</p>
            <p className="text-2xl font-bold">S/ {formatPrice(propertyData.precio_soles)}</p>
            <p className="text-sm">USD {formatPrice(propertyData.precio_dolares || 0)}</p>
          </div>
        </div>
        
        {/* QR Code */}
        <div className="absolute bottom-0 right-0">
          <div className="bg-white p-2">
            <QRCodeSVG 
              value={getPropertyUrl()} 
              size={80} 
              bgColor="#ffffff"
              fgColor="#000000"
              level="L"
            />
            <div className="text-xs text-center mt-1 font-medium text-gray-800">Escanea para más</div>
          </div>
        </div>
        
        {/* Ubicación */}
        <div className="absolute bottom-0 left-0 bg-black/70 text-white px-3 py-2 flex items-center">
          <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
          <span className="text-sm">{propertyData.ubicacion}</span>
        </div>
      </div>
      
      {/* Galería de imágenes pequeñas */}
      <div className="flex mt-2 mb-8 px-4 gap-4">
        {[...Array(Math.min(2, (propertyData.imagenes.length - 1) || 2))].map((_, index) => (
          <div key={index} className="flex-1 h-40">
            <PropertyImage 
              url={propertyData.imagenes[index + 1]}
              alt={`Interior ${index + 1}`} 
              className="w-full h-full object-cover rounded-md"
            />
          </div>
        ))}
      </div>
      
      {/* Características principales */}
      <div className="grid grid-cols-4 gap-0 border-t border-gray-100">
        {mainFeatures.map((feature, index) => (
          <div key={index} className="p-4 flex flex-col items-center justify-center border-r border-gray-100 last:border-r-0">
            <feature.icon className="w-6 h-6 text-orange-500 mb-2" />
            <span className="text-lg font-bold text-gray-800">{feature.value}</span>
            <span className="text-gray-500 text-xs">{feature.label}</span>
          </div>
        ))}
      </div>
      
      {/* Contenido principal */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-t border-gray-100">
        {/* Descripción y características */}
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
            <span className="bg-orange-100 p-1 rounded-full mr-2">
              <Home className="w-5 h-5 text-orange-500" />
            </span>
            Descripción
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            {propertyData.descripcion_corta}
          </p>
          
          <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
            <span className="bg-orange-100 p-1 rounded-full mr-2">
              <Check className="w-5 h-5 text-orange-500" />
            </span>
            Características
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {additionalFeatures.map((feature, index) => (
              <li key={index} className="flex items-center">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                <span className="text-gray-700 text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Información de contacto */}
        <div className="bg-gray-50 rounded-lg p-5">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Información de Contacto
          </h2>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-center">
              <div className="bg-orange-400 p-2 rounded-full mr-3 text-white">
                <Phone className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Teléfono</div>
                <div className="text-gray-800 font-medium">{propertyData.vendedor?.telefono || propertyData.telefono}</div>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="bg-orange-400 p-2 rounded-full mr-3 text-white">
                <Mail className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Email</div>
                <div className="text-gray-800 text-xs md:text-sm font-medium overflow-hidden text-ellipsis" style={{ maxWidth: '200px' }}>
                  {propertyData.vendedor?.email || propertyData.email}
                </div>
              </div>
            </div>
          </div>
          
          {/* Botón de acción */}
          <button className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center hover:bg-orange-600 transition-colors">
            <Calendar className="w-5 h-5 mr-2" />
            Agendar visita
          </button>
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-gray-800 text-white py-4 px-6">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-sm opacity-90">
            © {new Date().getFullYear()} InKayros | Todos los derechos reservados
          </div>
          <div className="text-sm">
            {getPropertyUrl()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicHomeFlyer;